import CIcon from '@coreui/icons-react';
import { CButton, CCol, CDropdown, CDropdownItem, CDropdownMenu, CFormGroup, CInput, CLabel } from '@coreui/react';
import _ from 'lodash';
import { reaction, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import React from 'react';
import enhanceWithClickOutside from 'react-click-outside';
import { v4 as generateRandomId } from 'uuid';

import { controller } from '../../core/Controllers/OrmController';
import { OrmStoreType } from '../../core/Stores/DirectoryStore';
import ValueFormatter from '../Formatters/ValueFormatter';
import NatSelection from '../Modals/NatSelection';
import { NatSelectionStoreType } from '../Modals/NatSelectionStore';
import { catalog } from '../support/Catalog';
import { models } from '../support/NatModels';
import { routesMap } from '../support/Routes';
import NatPolyRelationInputStore, { NatPolyRelationInputStoreType } from './InputsStore/NatPolyRelationInputStore';
import NatInputGroup from './NatInputGroup';
import NatValueField, { PropsType as ValueFieldPropsType } from './NatValueField';

interface PropsType<T> extends ValueFieldPropsType {
	goTo?: (path: string, state?: Record<string, unknown>, handlers?: Record<string, unknown>) => void;
	inputColumnClassName?: string;
	inputClassName?: string;
	type: string;
	invalid?: boolean;
	placeholder: string;
	formatter: ValueFormatter<T | undefined | null, string>;
	size: string;
	invalidFeedback?: string;
	autoComplete?: string;
	useHandleEnter?: boolean;
	relation: string;
	modelName?: string;
	include?: Array<string>;
	filter?: any;
	onlySelect?: boolean;
	filterWhere?: any;
	filterOrder?: any;
	disabled?: boolean;
	apiMethod?: () => any;
	relationProperty: string;
	availableModels: Array<string>;
	renderName?: (model: any) => string;
	or?: (value) => Array<any>;
	backspace?: (object, value) => void;
	mobile?: boolean;
	create?: (pluralName) => void;
	hierarchicalSelect?: boolean;
	treeSelect?: boolean;
}
interface InjectedProps extends PropsType<any> {
	directoryStore: OrmStoreType;
}

@inject('directoryStore')
@observer
class NatPolyRelationInput<T> extends NatValueField<T | undefined | null, string, PropsType<T>> {
	dropdownRef: any;
	dropdownActionsRef: any;
	natPolyRelationInputStore: NatPolyRelationInputStoreType;
	id: string;
	constructor(props: PropsType<T>) {
		super(props);
		this.dropdownRef = React.createRef();
		this.dropdownActionsRef = React.createRef();
		this.natPolyRelationInputStore = new NatPolyRelationInputStore();
		this.handleFocus = this.handleFocus.bind(this);
		this.handleKeyDown = this.handleKeyDown.bind(this);
		this.createFilter = this.createFilter.bind(this);
		this.handleClickOutside = this.handleClickOutside.bind(this);
		this.handleScrollToElement = this.handleScrollToElement.bind(this);
		this.toggleNatSelection = this.toggleNatSelection.bind(this);
		this.buildSelectedItems = this.buildSelectedItems.bind(this);
		this.saveItems = this.saveItems.bind(this);
		this.formModelsMetadata = this.formModelsMetadata.bind(this);
		this.setRelationProperty = this.setRelationProperty.bind(this);
		this.renderName = this.renderName.bind(this);
		this.setPlural = this.setPlural.bind(this);
		this.findAllRelations = this.findAllRelations.bind(this);
		this.resetRelation = this.resetRelation.bind(this);
		this.id = generateRandomId();
	}
	get injected() {
		return this.props as InjectedProps;
	}
	componentDidMount() {
		this.dropdownRef.current.querySelector('.nat__dropdown__list')?.addEventListener('scroll', this.handleScrollToElement);
		this.natPolyRelationInputStore.setValue(this.natPolyRelationInputStore, 'querySelector', this.dropdownRef.current.querySelector('.nat__dropdown__list'));
		this.setPlural();
		this.reactions.push(
			reaction(
				() => this.props.object[this.props.property],
				(value: T, previousValue: T) => {
					if (value !== previousValue) {
						if (_.isEmpty(this.props.object[this.props.relation]) && !_.isEmpty(this.props.object[this.props.property])) {
							let filter: any = {
								where: {
									id: this.props.object[this.props.property]
								}
							};
							if (!_.isEmpty(this.props.include)) {
								filter.include = this.props.include;
							}
							if (this.natPolyRelationInputStore.pluralName) {
								controller
									.findAll(this.natPolyRelationInputStore.pluralName, filter)
									.then((data) => {
										if (!_.isEmpty(data)) {
											runInAction(() => {
												this.props.object[this.props.relation] = data[0];
											});
											this.setState({
												value: this.renderValue(this.getValue())
											});
										}
									})
									.catch((error) => {
										catalog.handleNatError(error);
									});
							}
						}
						this.setState({
							value: this.renderValue(this.getValue())
						});
					}
				}
			)
		);
		this.reactions.push(
			reaction(
				() => this.props.object[this.props.relationProperty],
				(value: T, previousValue: T) => {
					if (value !== previousValue) {
						this.setPlural();
					}
				}
			)
		);
		this.addValidation(this.props.validation);
	}
	componentWillUnmount() {
		this.dropdownRef.current.querySelector('.nat__dropdown__list')?.removeEventListener('scroll', this.handleScrollToElement);
		this.natPolyRelationInputStore.setValue(this.natPolyRelationInputStore, 'querySelector', null);
		this.reactions.forEach((dispose) => dispose());
	}
	toggleDropDown() {
		this.natPolyRelationInputStore.setValue(this.natPolyRelationInputStore, 'relations', []);
		this.natPolyRelationInputStore.setValue(this.natPolyRelationInputStore, 'currentPage', 0);
		this.natPolyRelationInputStore.setValue(this.natPolyRelationInputStore, 'showDropdown', !this.natPolyRelationInputStore.showDropdown);
		if (this.natPolyRelationInputStore.pluralName) {
			this.createFilter();
		}
	}
	handleScrollToElement() {
		if (this.natPolyRelationInputStore.pluralName) {
			if (this.props.apiMethod === undefined) {
				let scrollTop = this.natPolyRelationInputStore.querySelector.scrollTop;
				let scrollHeight = this.natPolyRelationInputStore.querySelector.scrollHeight;
				let clientHeight = this.natPolyRelationInputStore.querySelector.clientHeight;
				let arr = this.natPolyRelationInputStore.relations;
				let itemsPerPage = this.natPolyRelationInputStore.itemsPerPage;
				let itemsListLength = arr.length;
				if (scrollTop >= scrollHeight - clientHeight && this.natPolyRelationInputStore.currentPage * itemsPerPage <= itemsListLength && scrollTop !== 0) {
					this.natPolyRelationInputStore.setValue(this.natPolyRelationInputStore, 'currentPage', this.natPolyRelationInputStore.currentPage + 1);
					this.natPolyRelationInputStore.setValue(this.natPolyRelationInputStore.filter, 'skip', this.natPolyRelationInputStore.currentPage * this.natPolyRelationInputStore.itemsPerPage);
					let filter = this.natPolyRelationInputStore.filter;
					controller
						.findAll(this.natPolyRelationInputStore.pluralName, filter)
						.then((data) => {
							if (!_.isEmpty(data)) {
								this.natPolyRelationInputStore.setValue(this.natPolyRelationInputStore, 'relations', this.natPolyRelationInputStore.relations.concat(data));
							}
						})
						.catch((error) => {
							catalog.handleNatError(error);
						});
				}
			}
		}
	}
	createFilter() {
		let filter: any = {
			skip: this.natPolyRelationInputStore.currentPage * this.natPolyRelationInputStore.itemsPerPage,
			limit: this.natPolyRelationInputStore.itemsPerPage,
			order: this.natPolyRelationInputStore.order
		};
		let defaultWhere = {};
		if (!_.isEmpty(this.props.filterWhere)) {
			Object.assign(defaultWhere, this.props.filterWhere);
		}
		filter.where = defaultWhere;
		if (!_.isEmpty(this.props.include)) {
			filter.include = this.props.include;
		}
		if (!_.isEmpty(this.props.filterOrder)) {
			filter.order = this.props.filterOrder;
		}
		this.natPolyRelationInputStore.setValue(this.natPolyRelationInputStore, 'filter', filter);
	}
	handleChange(e: React.ChangeEvent<HTMLInputElement>) {
		if (!_.get(this.props, 'onlySelect', false) && this.natPolyRelationInputStore.pluralName) {
			this.setState({
				value: e.target.value
			});
			this.natPolyRelationInputStore.setValue(this.natPolyRelationInputStore, 'currentPage', 0);
			this.natPolyRelationInputStore.querySelector.scrollTo(0, 0);
			clearTimeout(this.natPolyRelationInputStore.timer);
			this.natPolyRelationInputStore.setValue(
				this.natPolyRelationInputStore,
				'timer',
				setTimeout(() => {
					let or: Array<any> = [];
					if (this.props.or !== undefined) {
						or = this.props.or(this.state.value);
					} else {
						or = [
							{
								[`defaultName.${this.injected.directoryStore.models.language}`]: {
									like: this.state.value,
									options: 'i'
								}
							},
							{
								[`name.${this.injected.directoryStore.models.language}`]: {
									like: this.state.value,
									options: 'i'
								}
							},
							{
								sequenceNumber: {
									like: this.state.value
								}
							}
						];
					}
					this.natPolyRelationInputStore.setValue(this.natPolyRelationInputStore.filter, 'skip', 0);
					this.natPolyRelationInputStore.setValue(this.natPolyRelationInputStore.filter.where, 'or', or);
					this.findAllRelations();
				}, 500)
			);
			this.natPolyRelationInputStore.setValue(this.natPolyRelationInputStore, 'oldValue', e.target.value);
		}
	}
	handleClickOutside() {
		this.natPolyRelationInputStore.setValue(this.natPolyRelationInputStore, 'showDropdown', false);
	}
	handleFocus(event: any) {
		if (this.validate(this.props.object, event.target.value)) {
			this.natPolyRelationInputStore.setValue(this.natPolyRelationInputStore, 'oldValue', event.target.value);
		}
		event.target.select();
	}
	handleKeyDown(event: any) {
		if (event.keyCode === 13) {
			let form = event.target.form;
			if (form && form.length > 0) {
				let index = Array.prototype.indexOf.call(form, event.target);
				let length = form.length;
				let nextElement = null;
				for (index; index < length; index++) {
					let nextElement = _.get(form, `elements[${index + 1}]`, null);
					if (nextElement && !nextElement.disabled && nextElement.type !== 'checkbox') {
						nextElement.focus();
						break;
					} else {
						nextElement = null;
					}
				}
				if (!nextElement) {
					event.target.blur();
				}
				event.preventDefault();
				this.natPolyRelationInputStore.setValue(this.natPolyRelationInputStore, 'showDropdown', false);
			}
		} else if (event.keyCode === 8 || event.keyCode === 46) {
			if (_.get(this.props, 'onlySelect', false)) {
				this.resetRelation();
				this.natPolyRelationInputStore.setValue(this.natPolyRelationInputStore, 'oldValue', '');
			}
		}
	}
	parseValue(value: string): T | undefined | null {
		return this.props.formatter.parseValue(value);
	}

	renderValue(value: T | undefined): string {
		return this.props.formatter.renderValue(value, this.props.object, this.props.property, this.props.renderName);
	}

	setValue(model: any) {
		let object: any = this.props.object;
		if (this.validate(this.props.object, model)) {
			runInAction(() => {
				object[this.props.relation] = model;
				if (!_.isEmpty(model.id)) {
					object[this.props.property] = model.id;
				}
			});
			this.natPolyRelationInputStore.setValue(this.natPolyRelationInputStore, 'oldValue', this.props.renderName !== undefined ? this.props.renderName(model) : this.renderName(model));
		}
	}

	getValue(): T {
		let object: any = this.props.object;
		if (object[this.props.relation] !== undefined) {
			return object[this.props.relation];
		}
		return '' as any;
	}
	apiMethod() {
		if (this.props.apiMethod !== undefined) {
			this.props
				.apiMethod()
				.then((data) => {
					if (!_.isEmpty(data)) {
						this.natPolyRelationInputStore.setValue(this.natPolyRelationInputStore, 'relations', data);
					} else {
						this.natPolyRelationInputStore.setValue(this.natPolyRelationInputStore, 'relations', []);
					}
				})
				.catch((error) => {
					catalog.handleNatError(error);
				});
		}
	}
	toggleNatSelection(index: number) {
		const position = this.natPolyRelationInputStore.modals.indexOf(index);
		let newDetails = this.natPolyRelationInputStore.modals.slice();
		if (position !== -1) {
			newDetails.splice(position, 1);
		} else {
			newDetails.push(index);
		}
		this.natPolyRelationInputStore.setValue(this.natPolyRelationInputStore, 'modals', newDetails);
	}
	buildSelectedItems(arrayIds: Array<string>, store: NatSelectionStoreType) {
		const ids = arrayIds;
		const dict = _.chain(store.list).cloneDeep().keyBy('id').value();
		const currentSelectedItemsMap = _.chain(store.selectedItems).cloneDeep().keyBy('id').value();
		_.map(ids, (item: string) => {
			const selectedItem = dict[item];
			runInAction(() => {
				if (currentSelectedItemsMap[selectedItem.id] === undefined) {
					store.selectedItems.push(selectedItem);
				}
			});
		});
		const buffer = [] as Array<any>;
		let idsMap = _.chain(arrayIds)
			.keyBy((item) => {
				return item;
			})
			.value();
		_.map(store.selectedItems, (item: any) => {
			if (idsMap[item.id] !== undefined) {
				buffer.push(item);
			}
		});
		runInAction(() => {
			store.selectedItems = buffer;
		});
	}
	saveItems(store: NatSelectionStoreType) {
		if (!_.isEmpty(store.selectedItems)) {
			this.setValue(store.selectedItems[0]);
		}
	}
	formModelsMetadata() {
		let availableModels: Array<string> = this.props.availableModels;
		let modelsMetadata: Array<any> = [];
		for (let item of availableModels) {
			let data = {
				type: item,
				defaultName: { ru: models[item].displayName },
				plural: models[item].plural
			};
			modelsMetadata.push(data);
		}
		this.natPolyRelationInputStore.setValue(this.natPolyRelationInputStore, 'relations', modelsMetadata);
	}
	setRelationProperty(model: any) {
		let object: any = this.props.object;
		if (this.validate(this.props.object, model)) {
			runInAction(() => {
				object[this.props.relationProperty] = model.type;
			});
		}
		this.resetRelation();
		this.natPolyRelationInputStore.setValue(this.natPolyRelationInputStore, 'oldValue', '');
		this.findAllRelations();
	}
	renderName(data: any): string {
		if (!_.isEmpty(data)) {
			if (!_.isEmpty(data.displayName)) {
				return data.displayName[this.injected.directoryStore.models.language];
			} else if (!_.isEmpty(data.name)) {
				return data.name[this.injected.directoryStore.models.language];
			} else if (!_.isEmpty(data.defaultName)) {
				return data.defaultName[this.injected.directoryStore.models.language];
			} else {
				return `${models[this.props.object[this.props.relationProperty]].displayName} ${data.sequenceNumber} от ${new Date(data.periodAt).toLocaleDateString()}`;
			}
		} else {
			return '';
		}
	}
	setPlural() {
		if (!_.isEmpty(this.props.object[this.props.relationProperty])) {
			this.natPolyRelationInputStore.setValue(this.natPolyRelationInputStore, 'pluralName', models[this.props.object[this.props.relationProperty]].plural);
		}
	}
	findAllRelations() {
		if (this.natPolyRelationInputStore.pluralName) {
			let filter = this.natPolyRelationInputStore.filter;
			if (this.props.apiMethod !== undefined) {
				this.apiMethod();
			} else {
				controller
					.findAll(this.natPolyRelationInputStore.pluralName, filter)
					.then((data) => {
						if (!_.isEmpty(data)) {
							this.natPolyRelationInputStore.setValue(this.natPolyRelationInputStore, 'relations', data);
						} else {
							this.natPolyRelationInputStore.setValue(this.natPolyRelationInputStore, 'relations', []);
						}
					})
					.catch((error) => {
						catalog.handleNatError(error);
					});
			}
		}
	}
	resetRelation() {
		this.setState({
			value: ''
		});
		runInAction(() => {
			this.props.object[this.props.property] = null;
			delete this.props.object[this.props.relation];
		});
	}
	render() {
		return (
			<CDropdown innerRef={this.dropdownRef} className={_.isEmpty(this.props.label) ? 'nat__dropdown__without__label w-100 nat__dropdown' : 'w-100 nat__dropdown'}>
				<NatInputGroup mobile={this.props.mobile}>
					{(childrenProps) => (
						<CFormGroup row className={this.injected.directoryStore.models.windowSize < 490 ? 'w-100 align-items-center nat__form__group flex-nowrap' : 'w-100 align-items-center nat__form__group'}>
							{!_.isEmpty(this.props.label) && (
								<CLabel
									htmlFor={this.id}
									className={(() => {
										if (this.props.label) {
											if ((this.props.label.length > 13 && this.injected.directoryStore.models.windowSize < 490) || (this.props.label.length > 5 && this.injected.directoryStore.models.windowSize < 430)) {
												return 'align-self-center text-truncate text-left pl-3 m-0 nat__input__label__mobile_100';
											} else {
												return 'align-self-center text-truncate pl-3 m-0';
											}
										}
									})()}>
									{this.props.label}
								</CLabel>
							)}
							<CCol className={!_.isEmpty(this.props.label) ? 'align-self-center pr-0 d-flex' : 'align-self-center pr-0 pl-0 d-flex'}>
								<CInput
									id={this.id}
									invalid={this.props.invalid}
									type={this.props.type}
									name={this.props.property}
									placeholder={this.props.placeholder}
									value={this.state.value}
									onChange={this.handleChange}
									onFocus={this.handleFocus}
									size={this.props.size}
									className={this.props.inputClassName}
									autoComplete={this.props.autoComplete}
									onClick={(e) => {
										if (!this.natPolyRelationInputStore.showDropdown) {
											this.toggleDropDown();
											if (this.natPolyRelationInputStore.pluralName) {
												this.findAllRelations();
											} else {
												this.formModelsMetadata();
											}
										}
										e.stopPropagation();
									}}
									onBlur={() => {
										if (!this.natPolyRelationInputStore.showDropdown) {
											this.natPolyRelationInputStore.setValue(this.natPolyRelationInputStore, 'relations', []);
											this.natPolyRelationInputStore.setValue(this.natPolyRelationInputStore, 'currentPage', 0);
											this.createFilter();
										}
									}}
									onKeyDown={this.handleKeyDown}
									disabled={this.props.disabled}
								/>
								{childrenProps.isButtonShow && (
									<div
										className={(() => {
											if (this.props.mobile) {
												if (this.injected.directoryStore.models.windowSize > 766) {
													return 'nat__input__buttons';
												} else {
													return 'nat__input__buttons nat__input__buttons__mobile';
												}
											} else {
												return 'nat__input__buttons';
											}
										})()}>
										{_.map(
											this.props.formatter.getActionButtons(this.props.object, this.props.property, this.natPolyRelationInputStore.pluralName as string, [
												{
													icon: 'cil-loop',
													title: 'Сменить тип документа',
													onClick: () => {
														this.natPolyRelationInputStore.setValue(this.natPolyRelationInputStore, 'pluralName', null);
														runInAction(() => {
															this.props.object[this.props.relationProperty] = null;
														});
														this.formModelsMetadata();
														this.createFilter();
													},
													disable: true
												}
											]),
											(item, index) => {
												let button = (
													<CButton
														key={index}
														type="button"
														className={item.className ? item.className : 'nat__input__button'}
														title={item.title}
														onClick={() => {
															item.onClick(this.props.object, this.props.property, this.props.relation, this.natPolyRelationInputStore);
														}}
														to={item.to}>
														<CIcon name={item.icon} className="text-dark" size="sm" />
													</CButton>
												);
												if (item.disable) {
													if (!this.props.disabled) {
														return button;
													}
												} else {
													return button;
												}
											}
										)}
									</div>
								)}
							</CCol>
						</CFormGroup>
					)}
				</NatInputGroup>

				<CDropdownMenu className="rounded-0 py-0" show={this.natPolyRelationInputStore.showDropdown}>
					<div className="nat__dropdown__list mw-100 nat__dropdown__white__scrollbar">
						{(() => {
							return this.natPolyRelationInputStore.relations.map((item: any, index: number) => {
								return (
									<a
										key={index}
										className="dropdown-item"
										onClick={(e) => {
											if (item) {
												if (!_.isEmpty(this.natPolyRelationInputStore.pluralName)) {
													this.setValue(item);
													this.toggleDropDown();
												} else {
													this.setRelationProperty(item);
												}
											}
											e.stopPropagation();
										}}>
										<span className="text-truncate" title={this.props.renderName !== undefined ? this.props.renderName(item) : this.renderName(item)}>
											{this.props.renderName !== undefined ? this.props.renderName(item) : this.renderName(item)}
										</span>
									</a>
								);
							});
						})()}
					</div>
					{(() => {
						if (this.dropdownActionsRef.current !== undefined && this.dropdownActionsRef.current !== null) {
							if (!_.isEmpty(this.dropdownActionsRef.current.querySelectorAll('.dropdown__action__button'))) {
								return <CDropdownItem divider className="m-0" />;
							}
						}
					})()}
					<div className="d-flex justify-content-between bg-transparent dropdown-item p-0" ref={this.dropdownActionsRef}>
						{this.natPolyRelationInputStore.pluralName && this.natPolyRelationInputStore.pluralName !== 'enumerationItems' && !this.props.formatter.dontShowActionButtons && (
							<CButton
								onClick={(e) => {
									e.stopPropagation();
									this.toggleNatSelection(1);
									this.toggleDropDown();
								}}
								className="dropdown__action__button nat__button__hover">
								Показать все
							</CButton>
						)}
						{(() => {
							if (this.natPolyRelationInputStore.pluralName && routesMap[`/${this.natPolyRelationInputStore.pluralName}/:elementId`] !== undefined && !this.props.formatter.dontShowActionButtons) {
								if (this.props.create) {
									return (
										<CButton
											variant="ghost"
											onClick={(e) => {
												e.stopPropagation();
												if (this.props.create) {
													this.props.create(this.natPolyRelationInputStore.pluralName);
												}
											}}
											className="dropdown__action__button">
											<CIcon name="cil-plus" />
										</CButton>
									);
								} else {
									return (
										<CButton
											// to={`/${this.natPolyRelationInputStore.pluralName}/new`}
											onClick={() => {
												if (this.props.goTo) {
													this.props.goTo(
														`/${this.natPolyRelationInputStore.pluralName}/new`,
														{},
														{
															onSave: (model: T) => {
																if (!_.isEmpty(model)) {
																	this.setValue(model);
																}
																return Promise.resolve();
															}
														}
													);
												}
											}}
											className="dropdown__action__button nat__button__hover">
											<CIcon name="cil-plus" />
										</CButton>
									);
								}
							}
						})()}
					</div>
				</CDropdownMenu>
				{this.natPolyRelationInputStore.pluralName && this.natPolyRelationInputStore.modals.includes(1) && (
					<NatSelection
						filterWhere={this.props.filterWhere !== undefined ? this.props.filterWhere : {}}
						filterOrder={this.props.filterOrder !== undefined ? this.props.filterOrder : this.natPolyRelationInputStore.order}
						toggleNatSelection={this.toggleNatSelection}
						buildSelectedItems={this.buildSelectedItems}
						saveSelectedItems={this.saveItems}
						tabNumber={1}
						modals={this.natPolyRelationInputStore.modals}
						filterInclude={this.props.include !== undefined ? this.props.include : []}
						hierarchical={this.props.hierarchicalSelect}
						treeSelect={this.props.treeSelect}
						pluralName={this.natPolyRelationInputStore.pluralName}
						filterGroupOrder={[`name.${this.injected.directoryStore.models.language} ASC`]}
						filterGroupWhere={{ deleted: false, elementType: 1 }}
						singleSelect
						showCreateButton={routesMap[`/${this.natPolyRelationInputStore.pluralName}/:elementId`] !== undefined}
						createButton={(() => {
							if (routesMap[`/${this.natPolyRelationInputStore.pluralName}/:elementId`] !== undefined) {
								if (this.props.create) {
									return {
										title: 'СОЗДАТЬ',
										onClick: (e) => {
											e.stopPropagation();
											if (this.props.create) {
												this.props.create(this.natPolyRelationInputStore.pluralName);
											}
											this.toggleNatSelection(1);
										}
									};
								} else {
									return {
										title: 'СОЗДАТЬ',
										onClick: () => {
											this.toggleNatSelection(1);
										},
										to: `/${this.natPolyRelationInputStore.pluralName}/new`
									};
								}
							}
						})()}
						property="id"
					/>
				)}
			</CDropdown>
		);
	}
}

export default enhanceWithClickOutside(NatPolyRelationInput);
